<template>
  <div :class="['cast-finder', isLanding ? 'is-landing' : 'is-active']">
    <!-- persistent masthead: the same lockup morphs between hero and header -->
    <div class="masthead">
      <span class="logo">
        <svg class="logo-icon" viewBox="0 0 256 256" fill="var(--color-accent)"><path d="M216,104H102.09L210,75.51a8,8,0,0,0,5.68-9.84l-8.16-30a15.93,15.93,0,0,0-19.42-11.13L35.81,64.74a15.75,15.75,0,0,0-9.7,7.4,15.51,15.51,0,0,0-1.55,12L32,111.56c0,.14,0,.29,0,.44v88a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V112A8,8,0,0,0,216,104Z"></path></svg>
        <h1 class="wordmark">Cast Finder</h1>
      </span>
      <template v-if="!isLanding">
        <span class="header-divider appear"></span>
        <span class="status-line appear">
          <template v-if="mode === 'movies'">Finding <em>actors</em> in everything you pick</template>
          <template v-else>Finding <em>movies &amp; shows</em> starring everyone you pick</template>
        </span>
        <button class="btn btn-ghost start-over appear" @click="startOver">Start over</button>
      </template>
    </div>

    <p v-if="isLanding" class="subtitle" data-morph-fade>Pick movies, TV shows or actors — we find who and what they share.</p>

    <!-- persistent search bar: travels and resizes between hero and working position -->
    <div class="search-row">
      <SearchBar
        v-model="search"
        class="search-grow"
        :hero="isLanding"
        :placeholder="placeholder"
        :groups="resultGroups"
        :loading="loading"
        :no-results="noResults"
        @select="onSelectResult"
      />
    </div>

    <div v-if="isLanding" class="try-row" data-morph-fade>
      <span class="text-muted try-label">Try:</span>
      <button v-for="item in tryItems" :key="item.label" class="btn btn-secondary try-btn" @click="runTry(item)">{{ item.label }}</button>
    </div>

    <!-- chips share the graph's column so the results rail can start at the top -->
    <div class="content-row">
      <div class="main-col">
        <div v-if="!isLanding" class="tag-row">
          <span
            v-for="item in selections"
            :key="item.uid"
            :class="['tag', breakingIds.includes(item.uid) ? 'tag-neutral breaking' : 'tag-accent', 'chip']"
          >
            {{ chipLabel(item) }}
            <button class="chip-x" :aria-label="`Remove ${item.label}`" @click="removeItem(item.uid)">×</button>
          </span>
        </div>
        <ConstellationGraph
          :items="selections"
          :shared="sharedList"
          :mode="mode"
          :enrich="enrich"
          @pivot="pivot"
        />
      </div>
      <ResultsPanel
        v-if="!isLanding"
        data-morph-fade
        :items="selections"
        :shared="sharedList"
        :mode="mode"
        :moves="moves"
        :moves-loading="movesLoading"
        :recs="recs"
        :recs-loading="recsLoading"
        :rescue="rescue"
        @add="addEntity"
        @pivot="pivot"
        @remove="removeItem"
      />
    </div>
  </div>
</template>

<script>
  import invokeFunction from '../helper/invokeFunction';
  import {
    TV,
    characterOf,
    genreOf,
    isPerformance,
    isScripted,
    mediaTypeOf,
    normalizeCast,
    normalizeCredits,
    personUid,
    rankMixed,
    titleOf,
    uidOf,
    yearOf
  } from '../helper/tmdb';

  import ConstellationGraph from './ConstellationGraph.vue';
  import ResultsPanel from './ResultsPanel.vue';
  import SearchBar from './SearchBar.vue';

  const titleCastCache = new Map();
  const personCreditsCache = new Map();
  const personDetailsCache = new Map();
  const searchCache = new Map();
  const recommendationsCache = new Map();

  // typing forward and backing up again lands on queries we have already asked for
  function cachedSearch(functionName, query) {
    const key = `${functionName}:${query.toLowerCase()}`;
    if (!searchCache.has(key)) {
      const promise = invokeFunction(functionName, { search: query })
        .then(response => response.data.results || []);
      promise.catch(() => searchCache.delete(key));
      searchCache.set(key, promise);
    }
    return searchCache.get(key);
  }

  // TMDB repeats an entry when someone is credited more than once (dual roles, etc.);
  // duplicates would collide as sibling keys and clipPath ids in the chart
  function uniqueByUid(list) {
    const seen = new Set();
    return list.filter(entry => {
      if (seen.has(entry.uid)) return false;
      seen.add(entry.uid);
      return true;
    });
  }

  const TRY_ITEMS = [
    { label: 'Interstellar + The Martian', kind: 'movies', queries: [{ q: 'Interstellar', media: 'movie' }, { q: 'The Martian', media: 'movie' }] },
    { label: 'Inception + Peaky Blinders', kind: 'movies', queries: [{ q: 'Inception', media: 'movie' }, { q: 'Peaky Blinders', media: 'tv' }] },
    { label: 'Matt Damon + Anne Hathaway', kind: 'actors', queries: [{ q: 'Matt Damon', media: 'person' }, { q: 'Anne Hathaway', media: 'person' }] }
  ];

  export default {
    name: 'CastFinder',
    components: {
      ConstellationGraph,
      ResultsPanel,
      SearchBar
    },
    data() {
      return {
        mode: null,
        selections: [],
        search: '',
        rawMovies: [],
        rawActors: [],
        settledQuery: '',
        pending: 0,
        searchTimeout: null,
        moves: [],
        movesLoading: false,
        movesToken: 0,
        recs: [],
        recsLoading: false,
        enrich: {},
        morphSuppress: false
      };
    },
    computed: {
      isLanding() {
        return this.selections.length === 0;
      },
      loading() {
        return this.pending > 0;
      },
      placeholder() {
        if (!this.mode || this.selections.length === 0) {
          return 'Search movies, TV shows or actors…';
        }
        const noun = this.mode === 'movies' ? 'title' : 'actor';
        if (this.selections.length === 1) {
          return `Add a second ${noun} to intersect…`;
        }
        return `Add a ${noun}…`;
      },
      tryItems() {
        return TRY_ITEMS;
      },
      resultGroups() {
        const undecided = !this.mode;
        const cap = undecided ? 5 : 8;
        const movieItems = this.rawMovies
          .filter(title => title.poster_path)
          .slice(0, cap)
          .map(title => {
            const year = yearOf(title);
            const isTv = mediaTypeOf(title) === TV;
            return {
              id: uidOf(title),
              kind: 'movies',
              label: titleOf(title) || title.original_title,
              sub: [year && String(year), isTv && 'TV'].filter(Boolean).join(' · '),
              img: `https://image.tmdb.org/t/p/w92${title.poster_path}`,
              shape: 'square',
              raw: title
            };
          });
        const actorItems = this.rawActors
          .filter(person => person.profile_path)
          .slice(0, cap)
          .map(person => ({
            id: personUid(person),
            kind: 'actors',
            label: person.name,
            sub: (person.known_for || []).map(entry => entry.title || entry.name).filter(Boolean).slice(0, 2).join(', '),
            img: `https://image.tmdb.org/t/p/w92${person.profile_path}`,
            shape: 'circle',
            raw: person
          }));
        return [
          { key: 'movies', header: undecided ? 'Movies & TV · find their shared cast' : '', items: movieItems },
          { key: 'actors', header: undecided ? 'Actors · find their shared credits' : '', items: actorItems }
        ];
      },
      // only claim "no results" once the search for exactly what's typed has come
      // back; during the debounce or an in-flight request the dropdown stays quiet
      noResults() {
        const query = this.search.trim();
        return Boolean(query)
          && query === this.settledQuery
          && !this.loading
          && this.resultGroups.every(group => group.items.length === 0);
      },
      sharedList() {
        if (this.selections.length < 2) return [];
        const [first, ...rest] = this.selections;
        const matches = first.credits
          .filter(credit => rest.every(item => item.credits.some(other => other.uid === credit.uid)))
          .map(credit => ({
            ...credit,
            roles: this.selections
              .map(item => {
                const match = item.credits.find(other => other.uid === credit.uid);
                return match && characterOf(match);
              })
              .filter(Boolean)
          }));
        return rankMixed(matches);
      },
      breakingIds() {
        if (this.selections.length < 3 || this.sharedList.length) return [];
        return this.selections
          .filter(item => this.sharedWithout(item.uid).length > 0)
          .map(item => item.uid);
      },
      rescue() {
        if (!this.breakingIds.length) return null;
        const item = this.selections.find(entry => entry.uid === this.breakingIds[0]);
        const kept = this.sharedWithout(item.uid)
          .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
          .slice(0, 2);
        return { item, kept };
      }
    },
    watch: {
      isLanding() {
        if (this.morphSuppress || this.prefersReducedMotion()) return;
        const host = this.$el;
        const logoEl = host.querySelector('.logo');
        const searchEl = host.querySelector('.search-grow');
        const graphEl = host.querySelector('.graph-shell');
        const firstLogo = logoEl && logoEl.getBoundingClientRect();
        const firstSearch = searchEl && searchEl.getBoundingClientRect();
        const firstGraph = graphEl && graphEl.getBoundingClientRect();
        host.querySelectorAll('[data-morph-fade]').forEach(el => this.cloneFade(el));
        this.$nextTick(() => {
          const logo = host.querySelector('.logo');
          const searchBar = host.querySelector('.search-grow');
          const graph = host.querySelector('.graph-shell');
          if (logo && firstLogo) this.flipTravel(logo, firstLogo, true);
          if (searchBar && firstSearch) this.flipTravel(searchBar, firstSearch, false);
          if (graph && firstGraph) this.flipTravel(graph, firstGraph, false);
        });
      },
      search(value) {
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
          this.searchTimeout = null;
        }
        if (!value.trim()) {
          this.rawMovies = [];
          this.rawActors = [];
          this.settledQuery = '';
          return;
        }
        this.searchTimeout = setTimeout(() => this.runSearch(value.trim()), 400);
      },
      selections: {
        handler() {
          this.refreshMoves();
        },
        deep: false
      }
    },
    methods: {
      prefersReducedMotion() {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      },
      cloneFade(el) {
        const host = this.$el;
        const hostRect = host.getBoundingClientRect();
        const rect = el.getBoundingClientRect();
        const clone = el.cloneNode(true);
        clone.removeAttribute('data-morph-fade');
        clone.style.position = 'absolute';
        clone.style.left = `${rect.left - hostRect.left}px`;
        clone.style.top = `${rect.top - hostRect.top}px`;
        clone.style.width = `${rect.width}px`;
        clone.style.height = `${rect.height}px`;
        clone.style.margin = '0';
        clone.style.pointerEvents = 'none';
        clone.style.transition = 'opacity 0.24s ease';
        host.appendChild(clone);
        requestAnimationFrame(() => requestAnimationFrame(() => {
          clone.style.opacity = '0';
        }));
        setTimeout(() => clone.remove(), 320);
      },
      flipTravel(el, fromRect, withScale) {
        const to = el.getBoundingClientRect();
        const dx = fromRect.left - to.left;
        const dy = fromRect.top - to.top;
        el.style.transformOrigin = 'left top';
        el.style.transform = withScale
          ? `translate(${dx}px, ${dy}px) scale(${fromRect.height / to.height})`
          : `translate(${dx}px, ${dy}px)`;
        el.style.transition = 'none';
        if (!withScale) {
          el.style.width = `${fromRect.width}px`;
          el.style.flex = 'none';
        }
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const ease = 'cubic-bezier(0.3, 0.7, 0.3, 1)';
          el.style.transition = withScale
            ? `transform 0.6s ${ease}`
            : `transform 0.6s ${ease}, width 0.6s ${ease}`;
          el.style.transform = '';
          if (!withScale) el.style.width = `${to.width}px`;
          setTimeout(() => {
            el.style.transition = '';
            el.style.transform = '';
            el.style.transformOrigin = '';
            if (!withScale) {
              el.style.width = '';
              el.style.flex = '';
            }
          }, 700);
        }));
      },
      chipLabel(item) {
        const base = this.mode === 'movies' && item.year ? `${item.label} (${item.year})` : item.label;
        return this.breakingIds.includes(item.uid) ? `${base} — breaks the match` : base;
      },
      sharedWithout(excludeUid) {
        const others = this.selections.filter(item => item.uid !== excludeUid);
        if (others.length < 2) return [];
        const [first, ...rest] = others;
        return first.credits.filter(credit => rest.every(item => item.credits.some(other => other.uid === credit.uid)));
      },
      async runSearch(query) {
        this.pending += 1;
        try {
          let movies = [];
          let actors = [];
          if (this.mode === 'actors') {
            actors = await cachedSearch('searchCast', query);
          } else {
            // titles mode spans movies and TV, so /search/multi is both the one
            // request that covers them and — while the mode is undecided — the
            // one that covers people too
            const results = await cachedSearch('searchMulti', query);
            movies = results.filter(result => result.media_type === 'movie' || result.media_type === TV);
            actors = this.mode ? [] : results.filter(result => result.media_type === 'person');
          }
          if (this.search.trim() === query) {
            this.rawMovies = movies;
            this.rawActors = actors;
            this.settledQuery = query;
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.pending -= 1;
        }
      },
      // a movie and a show can share a numeric id, so the cache keys on the uid
      fetchTitleCast(title) {
        const uid = uidOf(title);
        if (!titleCastCache.has(uid)) {
          const mediaType = mediaTypeOf(title);
          const promise = (mediaType === TV
            ? invokeFunction('getCastForTvId', { tvId: title.id })
            : invokeFunction('getCastForMovieId', { movieId: title.id })
          ).then(response => normalizeCast(response.data.cast || [], mediaType));
          promise.catch(() => titleCastCache.delete(uid));
          titleCastCache.set(uid, promise);
        }
        return titleCastCache.get(uid);
      },
      // biography and birthday feed the "Your actors" card; TMDB bios run to
      // several paragraphs, so keep the first one — it reads as the summary
      fetchPersonDetails(personId) {
        if (!personDetailsCache.has(personId)) {
          const promise = invokeFunction('getDetailsForPersonId', { personId })
            .then(({ data }) => ({
              bio: (data.biography || '').split('\n').find(Boolean) || '',
              born: data.birthday || ''
            }));
          promise.catch(() => personDetailsCache.delete(personId));
          personDetailsCache.set(personId, promise);
        }
        return personDetailsCache.get(personId);
      },
      // TMDB's taste for the "If you like it" shelf; like the cast cache, keyed
      // on uid because a movie and a show can share a numeric id
      fetchRecommendations(item) {
        if (!recommendationsCache.has(item.uid)) {
          const promise = (item.mediaType === TV
            ? invokeFunction('getRecommendationsForTvId', { tvId: item.id })
            : invokeFunction('getRecommendationsForMovieId', { movieId: item.id })
          ).then(response => response.data.results || []);
          promise.catch(() => recommendationsCache.delete(item.uid));
          recommendationsCache.set(item.uid, promise);
        }
        return recommendationsCache.get(item.uid);
      },
      fetchPersonCredits(personId) {
        if (!personCreditsCache.has(personId)) {
          const promise = invokeFunction('getCreditsForPersonId', { personId })
            .then(response => normalizeCredits(response.data.cast || []));
          promise.catch(() => personCreditsCache.delete(personId));
          personCreditsCache.set(personId, promise);
        }
        return personCreditsCache.get(personId);
      },
      onSelectResult(result) {
        if (!this.mode) {
          this.mode = result.kind;
        }
        this.addEntity(result.raw);
      },
      async addEntity(raw) {
        const isMovies = this.mode === 'movies';
        const uid = isMovies ? uidOf(raw) : personUid(raw);
        if (this.selections.some(item => item.uid === uid)) return;
        // the search result already carries title/poster, so the node can land on the
        // chart right away and fill in its cast when the fetch returns
        const placeholder = isMovies
          ? {
            uid,
            id: raw.id,
            mediaType: mediaTypeOf(raw),
            label: titleOf(raw) || raw.original_title,
            year: yearOf(raw),
            genre: genreOf(raw),
            popularity: raw.popularity,
            poster: raw.poster_path || null,
            overview: raw.overview || '',
            credits: [],
            loading: true
          }
          : {
            uid,
            id: raw.id,
            label: raw.name,
            popularity: raw.popularity,
            profile: raw.profile_path || null,
            credits: [],
            loading: true
          };
        this.selections = [...this.selections, placeholder];
        this.resetSearch();
        if (!isMovies) {
          // not awaited: the card fills in the bio when it arrives, and a
          // failed details fetch shouldn't cost the selection itself
          this.fetchPersonDetails(raw.id)
            .then(details => {
              this.selections = this.selections.map(item =>
                item.uid === uid ? { ...item, ...details } : item
              );
            })
            .catch(error => console.error(error));
        }
        try {
          const credits = uniqueByUid(isMovies
            ? (await this.fetchTitleCast(raw)).filter(credit => credit.profile_path)
            : (await this.fetchPersonCredits(raw.id))
              .filter(credit => credit.poster_path && isScripted(credit) && isPerformance(credit)));
          this.selections = this.selections.map(item =>
            item.uid === uid ? { ...item, credits, loading: false } : item
          );
        } catch (error) {
          console.error(error);
          this.selections = this.selections.filter(item => item.uid !== uid);
        }
      },
      removeItem(uid) {
        this.selections = this.selections.filter(item => item.uid !== uid);
        if (!this.selections.length) {
          this.mode = null;
        }
      },
      resetSearch() {
        this.search = '';
        this.rawMovies = [];
        this.rawActors = [];
        this.settledQuery = '';
      },
      startOver() {
        this.selections = [];
        this.mode = null;
        this.resetSearch();
      },
      async pivot(credit) {
        this.morphSuppress = true;
        try {
          this.mode = this.mode === 'movies' ? 'actors' : 'movies';
          this.selections = [];
          this.resetSearch();
          await this.addEntity(credit);
        } finally {
          this.morphSuppress = false;
        }
      },
      async runTry(item) {
        this.mode = item.kind;
        this.pending += 1;
        try {
          for (const query of item.queries) {
            // one endpoint for all three media types, and it shares the search
            // cache with whatever the user types next
            const results = await cachedSearch('searchMulti', query.q);
            const hit = results.find(result =>
              result.media_type === query.media &&
              (query.media === 'person' ? result.profile_path : result.poster_path)
            );
            if (hit) await this.addEntity(hit);
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.pending -= 1;
        }
      },
      async refreshMoves() {
        const token = ++this.movesToken;
        if (this.selections.length !== 1 || this.selections[0].loading) {
          this.moves = [];
          this.movesLoading = false;
          this.enrich = {};
          this.recs = [];
          this.recsLoading = false;
          return;
        }
        const item = this.selections[0];
        this.movesLoading = true;
        this.moves = [];
        this.enrich = {};
        this.recs = [];
        this.recsLoading = this.mode === 'movies';
        try {
          const aggregate = new Map();
          // start alongside the credit fetches; a failed shelf shouldn't cost the moves
          const recsPromise = this.mode === 'movies'
            ? this.fetchRecommendations(item).catch(error => {
              console.error(error);
              return [];
            })
            : Promise.resolve([]);
          if (this.mode === 'movies') {
            const top = item.credits.slice(0, 6);
            const lists = await Promise.all(top.map(person => this.fetchPersonCredits(person.id)));
            if (token !== this.movesToken) return;
            const enrich = {};
            top.forEach((person, index) => {
              const titles = lists[index].filter(title =>
                title.poster_path && title.uid !== item.uid && isScripted(title) && isPerformance(title)
              );
              enrich[person.uid] = titles.length;
              titles.forEach(title => {
                const entry = aggregate.get(title.uid) || { entity: title, keeps: [] };
                if (!entry.keeps.includes(person.name)) entry.keeps.push(person.name);
                aggregate.set(title.uid, entry);
              });
            });
            this.enrich = enrich;
          } else {
            const top = rankMixed(item.credits).slice(0, 4);
            const lists = await Promise.all(top.map(title => this.fetchTitleCast(title)));
            if (token !== this.movesToken) return;
            top.forEach((title, index) => {
              lists[index]
                .filter(person => person.profile_path && person.uid !== item.uid)
                .slice(0, 12)
                .forEach(person => {
                  const entry = aggregate.get(person.uid) || { entity: person, keeps: [] };
                  const label = titleOf(title) || title.original_title;
                  if (!entry.keeps.includes(label)) entry.keeps.push(label);
                  aggregate.set(person.uid, entry);
                });
            });
          }
          const entries = [...aggregate.values()]
            .filter(entry => !this.selections.some(selected => selected.uid === entry.entity.uid));
          const byUid = new Map(entries.map(entry => [entry.entity.uid, entry]));
          // rank first, then sort by overlap: sort is stable, so entries tied on
          // overlap keep the ranking order rather than falling back to a raw
          // popularity that doesn't compare across media types
          this.moves = rankMixed(entries.map(entry => entry.entity))
            .map(entity => byUid.get(entity.uid))
            .sort((a, b) => b.keeps.length - a.keeps.length)
            .slice(0, 3);
          this.movesLoading = false;
          // annotate each rec with the cast it shares with the selection — the
          // aggregate above already knows which titles this cast appears in, so
          // the badge costs no extra requests. Overlap first (27a: scrolling
          // should be optional); the stable sort keeps TMDB's order within ties
          const annotated = (await recsPromise)
            .filter(rec => rec.poster_path)
            .map(rec => ({ ...rec, uid: uidOf(rec) }))
            .filter(rec => rec.uid !== item.uid)
            .map(rec => ({ ...rec, shared: (aggregate.get(rec.uid) || { keeps: [] }).keeps }));
          if (token !== this.movesToken) return;
          this.recs = annotated.sort((a, b) => b.shared.length - a.shared.length);
        } catch (error) {
          console.error(error);
        } finally {
          if (token === this.movesToken) {
            this.movesLoading = false;
            this.recsLoading = false;
          }
        }
      }
    }
  }
</script>

<style scoped>
  .cast-finder {
    position: relative;
    max-width: 1080px;
    margin: 0 auto;
    padding: 17px 22px 22px;
  }
  /* landing content floats at the viewport's vertical center; `safe` keeps the
     top reachable on short viewports (unsupported browsers fall back to center) */
  .cast-finder.is-landing {
    display: flex;
    flex-direction: column;
    justify-content: center;
    justify-content: safe center;
    min-height: 100vh;
    min-height: 100dvh;
  }

  /* masthead: one lockup, two layouts */
  .masthead {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .is-landing .masthead {
    justify-content: center;
    margin-bottom: 6px;
  }
  .is-active .masthead {
    border-bottom: 1px solid var(--color-neutral-800);
    padding-bottom: 14px;
    margin-bottom: 15px;
    animation: masthead-border 0.3s ease 0.45s backwards;
  }
  @keyframes masthead-border {
    from {
      border-color: transparent;
    }
  }
  .logo {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    white-space: nowrap;
  }
  .is-landing .logo {
    gap: 13px;
  }
  .logo-icon {
    width: 17px;
    height: 17px;
    flex: none;
  }
  .is-landing .logo-icon {
    width: 40px;
    height: 40px;
  }
  .wordmark {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.1;
  }
  .is-landing .wordmark {
    font-size: 46px;
    letter-spacing: -0.02em;
  }
  .header-divider {
    width: 1px;
    height: 20px;
    background: var(--color-neutral-800);
    flex: none;
  }
  .status-line {
    font-size: 13.5px;
    color: var(--color-neutral-400);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .status-line em {
    font-style: normal;
    color: var(--color-accent-300);
  }
  .start-over {
    font-size: 12px;
    margin-left: auto;
    white-space: nowrap;
  }
  .appear {
    animation: appear-fade 0.3s ease 0.45s backwards;
  }
  @keyframes appear-fade {
    from {
      opacity: 0;
    }
  }

  /* landing extras */
  .subtitle {
    font-size: 15px;
    color: var(--color-neutral-400);
    text-align: center;
    margin: 0 0 22px;
  }
  .try-row {
    display: flex;
    justify-content: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 11px;
  }
  .try-label {
    font-size: 12px;
    align-self: center;
  }
  .try-btn {
    font-size: 12px;
    white-space: nowrap;
  }

  /* search row: centered narrow on landing, full-width with chips when active */
  .search-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }
  .is-landing .search-row {
    /* as a flex item, auto margins shrink-wrap the row — claim the width first */
    width: 100%;
    max-width: 520px;
    margin: 0 auto;
  }
  .search-grow {
    flex: 1;
    min-width: 240px;
  }
  .tag-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin: 8px 0 14px;
  }
  .chip {
    gap: 6px;
    white-space: nowrap;
    animation: chip-in 0.35s cubic-bezier(0.34, 1.4, 0.64, 1) backwards;
  }
  @keyframes chip-in {
    from {
      opacity: 0;
      transform: scale(0.7);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  .chip.breaking {
    box-shadow: inset 0 0 0 1px var(--color-neutral-600);
  }
  .chip-x {
    border: 0;
    background: transparent;
    color: inherit;
    opacity: 0.6;
    cursor: pointer;
    font-size: 12px;
    padding: 0;
    line-height: 1;
  }
  .chip-x:hover {
    opacity: 1;
  }
  .content-row {
    display: flex;
    gap: 22px;
    align-items: stretch;
  }
  .main-col {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .is-landing .content-row {
    justify-content: center;
    margin-top: 14px;
  }
  .is-landing .main-col {
    flex: 0 1 560px;
  }

  @media (prefers-reduced-motion: reduce) {
    .chip,
    .appear,
    .is-active .masthead {
      animation: none;
    }
  }

  /* the chart is a fixed-aspect svg: capping the shell at 1080 leaves its text
     small on a big display, so let it keep growing */
  @media (min-width: 1400px) {
    .cast-finder {
      max-width: 1320px;
    }
  }

  @media (max-width: 860px) {
    .content-row {
      flex-direction: column;
    }
    .content-row :deep(.panel) {
      flex: none;
    }
    .status-line {
      display: none;
    }
    .header-divider {
      display: none;
    }
  }
</style>
